import {
  DocketDisplayItemTypeHeight,
  CONTINUE,
  headerOrder,
} from '../../../common/constants';
import {
  DocketDisplayItem,
  DocketDisplayItemType,
  DocketDisplayOrderItem,
} from '../../../common/types';
import { PaginatedDocketItems, isOverflowingDocket } from '../../layoutHelper';

export const getPaginatedDocketItems = (
  items: DocketDisplayItem[],
  scale: (n: number) => number,
  columnHeight: number,
  colsPerPage: number,
): PaginatedDocketItems[] => {
  // Pre-calculations and constants
  const colHeight = columnHeight;
  const continueHeight = scale(
    DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE],
  );
  const continuedHeight = scale(
    DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUED],
  );

  // Filter items once at the beginning
  const filteredItems = items.filter(
    item =>
      item.type !== DocketDisplayItemType.CONTINUE &&
      item.type !== DocketDisplayItemType.CONTINUED,
  );

  // Group items by docket ID
  const docketMap = new Map<
    string,
    {
      items: DocketDisplayItem[];
      totalHeight: number;
    }
  >();

  // Group items and calculate initial heights
  filteredItems.forEach(item => {
    let docketId = '';
    if (item.type === DocketDisplayItemType.DOCKET_HEADER) {
      docketId = (item as DocketDisplayOrderItem).id;
    } else if ((item as DocketDisplayOrderItem).docketId) {
      docketId = (item as DocketDisplayOrderItem).docketId;
    }

    if (docketId) {
      if (!docketMap.has(docketId)) {
        docketMap.set(docketId, { items: [], totalHeight: 0 });
      }
      const docketData = docketMap.get(docketId)!;
      docketData.items.push(item);
      docketData.totalHeight += scale(item.height);
    }
  });

  // Do a simulation pass to determine how many columns each docket will actually use
  const docketTotalColumns = new Map<string, number>();

  // Function to simulate column layout and count needed columns
  const simulateColumnLayout = () => {
    let simCurrentColumn = 1;
    let simCurrentHeight = 0;
    const simDocketColumns = new Map<string, number>();
    const simCurrentColumnForDocket = new Map<string, number>();

    // Initialize column counters for all dockets
    docketMap.forEach((_, docketId) => {
      simDocketColumns.set(docketId, 1);
      simCurrentColumnForDocket.set(docketId, 1);
    });

    let simPreviousDocketId: string | undefined;

    // Simulate processing items to calculate actual column counts
    filteredItems.forEach((item, index) => {
      let docketId: string | undefined;

      if (item.type === DocketDisplayItemType.DOCKET_HEADER) {
        docketId = (item as DocketDisplayOrderItem).id;
        simPreviousDocketId = docketId;
        // Reset current column for this docket
        if (docketId) {
          simCurrentColumnForDocket.set(docketId, 1);
        }
      } else if (simPreviousDocketId) {
        docketId = simPreviousDocketId;
      }

      const itemHeight = scale(item.height);

      // Check if we need a new column
      const willOverflow =
        isOverflowingDocket(
          filteredItems,
          index,
          colHeight,
          simCurrentHeight,
          continueHeight,
          scale,
        ) || simCurrentHeight + itemHeight > colHeight;

      if (willOverflow) {
        // Increment column counter for this docket if there are more items
        if (docketId) {
          const currentColumnNumber =
            simCurrentColumnForDocket.get(docketId) || 1;
          simDocketColumns.set(
            docketId,
            Math.max(
              simDocketColumns.get(docketId) || 1,
              currentColumnNumber + 1,
            ),
          );
          simCurrentColumnForDocket.set(docketId, currentColumnNumber + 1);
        }

        // Move to next column
        simCurrentColumn++;
        simCurrentHeight = continuedHeight; // Reset height but account for CONTINUED tag

        // Check if we need a new page
        if (simCurrentColumn - 1 >= colsPerPage) {
          simCurrentColumn = 1;
        }
      }

      // Add item height
      simCurrentHeight += itemHeight;
    });

    // Return the calculated column counts
    return simDocketColumns;
  };

  // Run simulation to get accurate column counts
  const simulatedColumnCounts = simulateColumnLayout();

  // Apply simulation results
  simulatedColumnCounts.forEach((totalCols, docketId) => {
    docketTotalColumns.set(docketId, totalCols);
  });

  // Initialize page structure for actual processing
  let currentPage = 1;
  let currentColumn = 1;
  let currentHeight = 0;
  let currentRowNumber = 1;
  let currentColumnObject = {
    column: currentColumn,
    rows: [{ row: currentRowNumber, items: [] as DocketDisplayItem[] }],
  };
  const pages = [{ page: currentPage, columns: [currentColumnObject] }];

  // Helper function to get current row
  const getCurrentRow = () => {
    return currentColumnObject.rows[currentColumnObject.rows.length - 1];
  };

  // Tracking variables
  let previousDocketId: string | undefined;
  let previousRowPosition: number | undefined;
  const currentDocketColumnNumber = new Map<string, number>();
  const remainingItemsByDocket = new Map<string, number>();

  // Initialize remaining items count and current column number
  docketMap.forEach(({ items }, docketId) => {
    remainingItemsByDocket.set(docketId, items.length);
    currentDocketColumnNumber.set(docketId, 1);
  });

  // Process items in a single pass
  filteredItems.forEach((item, index) => {
    // Extract docket information
    let docketId: string | undefined;
    let rowPosition = 1;

    if (item.type === DocketDisplayItemType.DOCKET_HEADER) {
      docketId = (item as DocketDisplayOrderItem).id;
      rowPosition = item?.position?.rowPosition || 1;
      previousDocketId = docketId;
      previousRowPosition = rowPosition;
    } else if (previousDocketId) {
      docketId = previousDocketId;
      rowPosition = previousRowPosition || 1;
    }

    // Calculate if this is the last item for the current docket
    const isLastItemForDocket = docketId
      ? (remainingItemsByDocket.get(docketId) || 0) === 1
      : false;

    // Check if we need a new column
    const itemHeight = scale(item.height);

    // If this is the last item for the docket, we don't need space for the CONTINUE tag
    const heightNeeded = isLastItemForDocket
      ? itemHeight
      : itemHeight + continueHeight;

    const needNewColumn =
      isOverflowingDocket(
        filteredItems,
        index,
        colHeight,
        currentHeight,
        continueHeight,
        scale,
      ) ||
      // More generous space check for the last item of a docket
      (currentHeight + heightNeeded > colHeight && !isLastItemForDocket) ||
      // Stricter space check for continuing items
      currentHeight + itemHeight > colHeight;

    // Stop CONTINUE/CONTINUED logic for header items
    const isHeaderItem = headerOrder.includes(item.type);

    if (needNewColumn) {
      // Check if there are more items for this docket after this one
      const hasMoreItemsForDocket = docketId
        ? (remainingItemsByDocket.get(docketId) || 0) > 1
        : false;

      // Only add CONTINUE tag if not a header item, we have a docket ID, and there are more items for this docket
      if (!isHeaderItem && docketId && hasMoreItemsForDocket) {
        // Get the current column number and total columns for this docket
        const columnNumber = currentDocketColumnNumber.get(docketId) || 1;
        const totalColumns = docketTotalColumns.get(docketId) || 1;

        // Add CONTINUE tag to current row
        getCurrentRow().items.push({
          type: DocketDisplayItemType.CONTINUE,
          name: CONTINUE,
          height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE],
          docketId,
          position: {
            parentRowPosition: rowPosition,
            pageIndicator: `${columnNumber}/${totalColumns}`,
          },
        });

        // Increment the current column number for this docket
        currentDocketColumnNumber.set(docketId, columnNumber + 1);
      }

      // Create new column on same page
      currentColumn++;
      currentRowNumber = 1;
      currentColumnObject = {
        column: currentColumn,
        rows: [{ row: currentRowNumber, items: [] as DocketDisplayItem[] }],
      };
      pages[currentPage - 1].columns.push(currentColumnObject);

      // Only add CONTINUED tag if we added a CONTINUE tag
      if (!isHeaderItem && docketId && hasMoreItemsForDocket) {
        // Get the current column number and total columns for this docket
        const columnNumber = currentDocketColumnNumber.get(docketId) || 1;
        const totalColumns = docketTotalColumns.get(docketId) || 1;

        // Add CONTINUED tag to new row
        getCurrentRow().items.push({
          type: DocketDisplayItemType.CONTINUED,
          name: CONTINUE,
          height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUED],
          docketId,
          position: {
            parentRowPosition: rowPosition,
            pageIndicator: `${columnNumber}/${totalColumns}`,
          },
        });
        currentHeight = continuedHeight;
      } else {
        currentHeight = 0;
      }
    }

    // Check if current page is full and create new page if needed
    if (currentColumn - 1 >= colsPerPage) {
      // New page if columns exceed limit
      currentPage++;
      currentColumn = 1;
      currentRowNumber = 1;
      currentColumnObject = {
        column: currentColumn,
        rows: [{ row: currentRowNumber, items: [] as DocketDisplayItem[] }],
      };
      pages.push({ page: currentPage, columns: [currentColumnObject] });

      // Check if there are more items for this docket after this one
      const hasMoreItemsForDocket = docketId
        ? (remainingItemsByDocket.get(docketId) || 0) > 1
        : false;

      // Add CONTINUED tag if applicable - but not for header items and only if there are more items
      if (!isHeaderItem && docketId && hasMoreItemsForDocket) {
        // Get the current column number and total columns for this docket
        const columnNumber = currentDocketColumnNumber.get(docketId) || 1;
        const totalColumns = docketTotalColumns.get(docketId) || 1;

        getCurrentRow().items.push({
          type: DocketDisplayItemType.CONTINUED,
          name: CONTINUE,
          height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUED],
          docketId,
          position: {
            parentRowPosition: rowPosition,
            pageIndicator: `${columnNumber}/${totalColumns}`,
          },
        });
        currentHeight = continuedHeight;
      } else {
        currentHeight = 0;
      }
    }

    // Add the current item to the current row
    getCurrentRow().items.push(item);
    currentHeight += itemHeight;

    // Decrement the remaining items counter for this docket
    if (docketId) {
      remainingItemsByDocket.set(
        docketId,
        (remainingItemsByDocket.get(docketId) || 1) - 1,
      );
    }
  });

  return pages;
};
